import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Search, Timer, Database, Crosshair, ChevronDown, ChevronRight, Repeat } from 'lucide-react';
import { authedFetch } from '@/lib/api';
import { logger } from '@/lib/logger';
import { cn, formatDuration, formatPercentage, getMetricColor } from '@/lib/utils';
import { RunHistory } from './eval/RunHistory';
import { MetricCards } from './eval/MetricCards';
import { ConfusionMatrix } from './eval/ConfusionMatrix';
import { VenueResults } from './eval/VenueResults';
import { RerankerDamage } from './eval/RerankerDamage';
import { DatasetBarChart } from './eval/DatasetBarChart';
import { PhaseComparisonChart } from './eval/PhaseComparisonChart';
import type { SearchEvalRun, DatasetResult } from '@/types/dashboard';

function DatasetResultCard({ result, defaultExpanded, isMultiRun }: { result: DatasetResult; defaultExpanded: boolean; isMultiRun: boolean }) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (result.status === 'failed') {
    return (
      <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 backdrop-blur-xs">
        <div className="font-semibold text-red-400">{result.dataset_name}</div>
        <div className="text-xs text-red-400/70 mt-1">{result.error}</div>
      </div>
    );
  }

  const p2 = result.phase2;
  if (!p2) return null;

  return (
    <div className="rounded-xl border border-primary/20 bg-card/50 backdrop-blur-xs overflow-hidden">
      {/* Header — always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center gap-4 hover:bg-primary/5 transition-colors"
      >
        <div className="shrink-0">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-primary" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
        <div className="flex-1 text-left min-w-0">
          <div className="font-semibold text-sm text-foreground truncate">
            {result.dataset_name}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {result.total_positives}P / {result.total_negatives}N
            {result.duration_ms ? ` · ${formatDuration(result.duration_ms)}` : ''}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-center">
            <div className={cn('text-sm font-bold tabular-nums', getMetricColor(p2.precision))}>
              {formatPercentage(p2.precision, 0)}
            </div>
            <div className="text-[9px] text-muted-foreground uppercase">Prec</div>
          </div>
          <div className="text-center">
            <div className={cn('text-sm font-bold tabular-nums', getMetricColor(p2.recall))}>
              {formatPercentage(p2.recall, 0)}
            </div>
            <div className="text-[9px] text-muted-foreground uppercase">Rec</div>
          </div>
          <div className="text-center">
            <div className={cn('text-sm font-bold tabular-nums', getMetricColor(p2.f1))}>
              {formatPercentage(p2.f1, 0)}
            </div>
            <div className="text-[9px] text-muted-foreground uppercase">F1</div>
          </div>
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-primary/10">
          <div className="pt-4 grid grid-cols-2 gap-4">
            <ConfusionMatrix
              phase={result.phase1!}
              title="Phase 1 — Semantic Search"
              candidateCount={result.phase1_candidates ?? 0}
              isMultiRun={isMultiRun}
            />
            <ConfusionMatrix
              phase={result.phase2!}
              title="Phase 2 — After Reranking"
              candidateCount={result.phase2_results ?? 0}
              isMultiRun={isMultiRun}
            />
          </div>
          {result.venue_results && result.venue_results.length > 0 && (
            <VenueResults venues={result.venue_results} datasetName={result.dataset_name} isMultiRun={isMultiRun} />
          )}
          {result.reranker_damage && result.reranker_damage.length > 0 && (
            <RerankerDamage damage={result.reranker_damage} />
          )}
        </div>
      )}
    </div>
  );
}

export default function EvaluationDashboard() {
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [run, setRun] = useState<SearchEvalRun | null>(null);
  const [loading, setLoading] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadRun = useCallback(
    async (runId: string, isInitial = false) => {
      if (isInitial) setLoading(true);
      try {
        const response = await authedFetch(
          `/api/evaluation/search-eval/runs/${runId}`
        );
        if (response.ok) {
          const data: SearchEvalRun = await response.json();
          setRun(data);

          // Dispatch event when run completes so sidebar can refresh
          if (data.status === 'completed' || data.status === 'failed') {
            window.dispatchEvent(new Event('evaluation-complete'));
          }
        }
      } catch (err) {
        logger.error('Failed to load run:', err);
      } finally {
        if (isInitial) setLoading(false);
      }
    },
    []
  );

  const handleSelectRun = (runId: string) => {
    setSelectedRunId(runId);
    loadRun(runId, true);
  };

  // Poll for updates while run is in progress
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (!selectedRunId || (run && run.status !== 'running')) return;

    pollRef.current = setInterval(() => {
      loadRun(selectedRunId);
    }, 5000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [selectedRunId, run?.status, loadRun]);

  return (
    <div className="flex h-full">
      {/* Run History Sidebar */}
      <aside className="w-72 border-r border-primary/30 glass-header shrink-0 p-4">
        <RunHistory
          onSelectRun={handleSelectRun}
          currentRunId={selectedRunId ?? undefined}
        />
      </aside>

      {/* Main Dashboard Area */}
      <div className="flex-1 overflow-y-auto p-6">
        {!selectedRunId ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Search className="h-16 w-16 text-muted-foreground/20 mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Select an Evaluation Run
            </h3>
            <p className="text-muted-foreground">
              Choose a run from the sidebar to view precision/recall analysis
            </p>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-muted-foreground">Loading results...</p>
            </div>
          </div>
        ) : run ? (
          <div className="space-y-6 max-w-[1400px] mx-auto">
            {/* Header Info Bar */}
            <div className="rounded-xl border border-primary/20 bg-card/50 p-4 backdrop-blur-xs">
              <div className={cn('grid gap-4', (run.runs_per_dataset ?? 1) > 1 ? 'grid-cols-5' : 'grid-cols-4')}>
                <div className="flex items-center gap-2">
                  <Database className="h-4 w-4 text-primary shrink-0" />
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground">Datasets</div>
                    <div className="font-mono text-primary text-sm">
                      {run.total_datasets ?? 0} dataset{(run.total_datasets ?? 0) !== 1 ? 's' : ''}
                      {run.status === 'running' && (
                        <span className="text-muted-foreground ml-1">
                          ({run.completed_datasets ?? 0}/{run.total_datasets ?? 0})
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {(run.runs_per_dataset ?? 1) > 1 && (
                  <div className="flex items-center gap-2">
                    <Repeat className="h-4 w-4 text-primary shrink-0" />
                    <div className="min-w-0">
                      <div className="text-xs text-muted-foreground">Runs/Dataset</div>
                      <div className="font-mono text-primary text-sm">
                        {run.runs_per_dataset}x each
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Timer className="h-4 w-4 text-primary shrink-0" />
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground">Duration</div>
                    <div className="font-mono text-primary text-sm">
                      {run.duration_ms > 0 ? formatDuration(run.duration_ms) : '--'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Crosshair className="h-4 w-4 text-primary shrink-0" />
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground">
                      Ground Truth
                    </div>
                    <div className="font-mono text-primary text-sm">
                      {run.total_positives ?? 0}+ / {run.total_negatives ?? 0}&minus;
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Search className="h-4 w-4 text-primary shrink-0" />
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground">Results</div>
                    <div className="font-mono text-primary text-sm">
                      {run.phase1_candidates ?? 0} candidates &rarr; {run.phase2_results ?? 0} final
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Running indicator with progress */}
            {run.status === 'running' && (
              <div className="rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-4 backdrop-blur-xs">
                <div className="flex items-center gap-3 mb-2">
                  <Loader2 className="h-5 w-5 animate-spin text-yellow-400" />
                  <div>
                    <div className="text-sm font-semibold text-yellow-400">
                      Evaluation in progress — {run.completed_datasets ?? 0} of {run.total_datasets ?? 0} datasets
                    </div>
                    <div className="text-xs text-yellow-400/70">
                      Results update automatically every 5 seconds
                    </div>
                  </div>
                </div>
                {(run.total_datasets ?? 0) > 0 && (
                  <div className="w-full h-2 bg-yellow-500/20 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-yellow-400 rounded-full transition-all duration-500"
                      style={{ width: `${((run.completed_datasets ?? 0) / (run.total_datasets ?? 1)) * 100}%` }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Failed indicator */}
            {run.status === 'failed' && (
              <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 backdrop-blur-xs flex items-center gap-3">
                <div className="text-sm font-semibold text-red-400">
                  Evaluation failed
                </div>
              </div>
            )}

            {/* Aggregate Metrics (show when we have results) */}
            {run.phase2 && (run.status === 'completed' || (run.completed_datasets ?? 0) > 0) && (
              <>
                <MetricCards
                  phase2={run.phase2}
                  rerankerDamageCount={run.reranker_damage?.length ?? 0}
                  runsPerDataset={run.runs_per_dataset}
                />

                {/* Charts — the main visual overview */}
                {run.dataset_results && run.dataset_results.length > 1 && (
                  <div className="grid grid-cols-2 gap-6">
                    <DatasetBarChart
                      datasets={run.dataset_results}
                      aggregateF1={run.phase2.f1}
                    />
                    <PhaseComparisonChart datasets={run.dataset_results} />
                  </div>
                )}

                {/* Single dataset: show chart full-width */}
                {run.dataset_results && run.dataset_results.length === 1 && (
                  <DatasetBarChart
                    datasets={run.dataset_results}
                    aggregateF1={run.phase2.f1}
                  />
                )}

                {/* Aggregate Confusion Matrices */}
                {run.phase1 && (
                  <div className="grid grid-cols-2 gap-6">
                    <ConfusionMatrix
                      phase={run.phase1}
                      title="Phase 1 — Semantic Search (Aggregate)"
                      candidateCount={run.phase1_candidates ?? 0}
                      isMultiRun={(run.runs_per_dataset ?? 1) > 1}
                    />
                    <ConfusionMatrix
                      phase={run.phase2}
                      title="Phase 2 — After Reranking (Aggregate)"
                      candidateCount={run.phase2_results ?? 0}
                      isMultiRun={(run.runs_per_dataset ?? 1) > 1}
                    />
                  </div>
                )}
              </>
            )}

            {/* Per-Dataset Detail (collapsible) */}
            {run.dataset_results && run.dataset_results.length > 0 && (
              <div>
                <h3 className="text-lg font-bold bg-linear-to-r from-primary to-orange-400 bg-clip-text text-transparent mb-4">
                  Per-Dataset Detail
                </h3>
                <div className="space-y-3">
                  {run.dataset_results.map((result) => (
                    <DatasetResultCard
                      key={result.dataset_id}
                      result={result}
                      defaultExpanded={run.dataset_results.length === 1}
                      isMultiRun={(run.runs_per_dataset ?? 1) > 1}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Aggregate Reranker Damage */}
            {run.status === 'completed' && run.reranker_damage && run.reranker_damage.length > 0 && (
              <RerankerDamage damage={run.reranker_damage} />
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
